import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectCurrentCanvas,
  selectCurrentCanvasExpertSnapshot,
  selectCurrentRectangles,
  selectIsCurrentUserWorkspaceOwner,
  selectSelectedCanvasId,
} from "@store/selectors";
import { saveCanvasAssessment } from "@store/thunks/workspaceThunks";

const CANVAS_ASSESSMENT_AUTOSAVE_DELAY_MS = 1500;

/**
 * Stage 6.6: debounced auto-save of the contributor's canvas assessment.
 *
 * Mirrors useAssessmentAutoSave for the canvas. Watches the inputs that
 * influence the computed grade (student rectangles + expert canvas
 * snapshot) and fires saveCanvasAssessment 1.5s after the last change
 * settles.
 *
 * Guards: no selected canvas, owner-of-this-workspace (self-vs-self
 * trivial), no expert reference yet, or empty student canvas all
 * short-circuit before scheduling a timer.
 */
export function useCanvasAssessmentAutoSave(): void {
  const dispatch = useAppDispatch();
  const selectedCanvasId = useAppSelector(selectSelectedCanvasId);
  const rectangles = useAppSelector(selectCurrentRectangles);
  const expertSnapshot = useAppSelector(selectCurrentCanvasExpertSnapshot);
  const isOwner = useAppSelector(selectIsCurrentUserWorkspaceOwner);
  // Subscribe to the canvas itself so arrow edits also trigger the
  // hook (arrows are nested inside rectangles, so the array ref
  // changes when arrow data does — but listing it here gives the
  // effect a stable dependency to lean on).
  const canvas = useAppSelector(selectCurrentCanvas);

  useEffect(() => {
    if (!selectedCanvasId) return;
    if (isOwner) return;
    if (!expertSnapshot || expertSnapshot.rectangles.length === 0) return;
    if (rectangles.length === 0) return;

    const timer = setTimeout(() => {
      dispatch(saveCanvasAssessment());
    }, CANVAS_ASSESSMENT_AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [
    dispatch,
    selectedCanvasId,
    rectangles,
    expertSnapshot,
    isOwner,
    canvas,
  ]);
}
